import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [validatingToken, setValidatingToken] = useState(true);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      setValidatingToken(false);
      return;
    }
    
    // Validate token
    const validateToken = async () => {
      try {
        await axios.get(`${API}/auth/validate-reset-token/${token}`);
        setValidatingToken(false);
      } catch (err) {
        setError('This password reset link has expired or is invalid. Please request a new one.');
        setValidatingToken(false);
      }
    };
    
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          <p className="text-muted-foreground">Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="reset-password-error">
        <div className="w-full max-w-md">
          <div className="glass-panel rounded-2xl p-8 border text-center">
            <XCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#ef4444' }} />
            <h1 className="text-2xl font-bold mb-4" style={{ color: '#ffffff' }}>Link Expired</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/forgot-password">
              <Button 
                className="rounded-full"
                style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
              >
                Request New Reset Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="reset-password-success">
        <div className="w-full max-w-md">
          <div className="glass-panel rounded-2xl p-8 border text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
            <h1 className="text-2xl font-bold mb-4" style={{ color: '#ffffff' }}>Password Reset!</h1>
            <p className="text-muted-foreground mb-6">
              Your password has been successfully reset. You can now login with your new password.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="rounded-full"
              style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
              data-testid="go-to-login-button"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="glass-panel rounded-2xl p-8 border">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
              <Lock size={32} style={{ color: '#D4AF37' }} />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }} data-testid="reset-password-title">
              Reset Your Password
            </h1>
            <p className="text-muted-foreground">
              Enter your new password below.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={6}
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={6}
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full rounded-full transition-all duration-300"
              disabled={loading}
              style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
              data-testid="reset-password-submit-button"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
